import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, Container, Skeleton, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Tooltip, IconButton, Alert, Button, TextField, InputAdornment,
  TablePagination
} from '@mui/material';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';

// Configs & Utils
import { masterDataFields, getTableColumns } from '../utils/masterDataConfig';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { ROLES, normalizeRole } from '../utils/roles';

// Modals
import CreateEditMasterModal from '../components/Modals/CreateEditMasterModal';
import MasterDataDeleteModal from '../components/Modals/MasterDataDeleteModal';

// Direct Explicit Service Imports
import { brandService } from '../services/brandService';
import { categoryService } from '../services/categoryService';
import { subCategoryService } from '../services/subCategoryService';
import { departmentService } from '../services/departmentService';
import { vendorService } from '../services/vendorService';
import { locationService } from '../services/locationService';
import { singleValueListService } from '../services/singleValueListService'; 
import { customerService } from '../services/customerService';

// Standardized UI Service Map — aligned to actual service method signatures
const SERVICE_MAP = {
  brands: {
    getItems:   ()   => brandService.fetchBrands(),
    deleteItem: (id) => brandService.delete(id),
  },
  categories: {
    getItems:   ()   => categoryService.fetchCategories(),
    deleteItem: (id) => categoryService.delete(id),
  },
  subcategories: {
    getItems:   ()   => subCategoryService.fetchSubCategories(),
    deleteItem: (id) => subCategoryService.delete(id),
  },
  departments: {
    getItems:   ()   => departmentService.fetchDepartments(),
    deleteItem: (id) => departmentService.delete(id),
  },
  vendors: {
    getItems:   ()   => vendorService.fetchVendors(),
    deleteItem: (id) => vendorService.delete(id),
  },
  customers: {
    getItems:   ()   => customerService.fetchCustomers(),
    deleteItem: (id) => customerService.delete(id),
  },
  locations: {
    getItems:   ()   => locationService.fetchLocations(),
    deleteItem: (id) => locationService.delete(id),
  },
};

const TABS = [
  { key: 'brands',        title: 'Brand' },
  { key: 'categories',    title: 'Category' },
  { key: 'subcategories', title: 'Sub-Category' },
  { key: 'departments',   title: 'Department' },
  { key: 'vendors',       title: 'Vendor' },
   { key: 'customers',     title: 'Customer' },
  { key: 'locations',     title: 'Location' },
];

const SUB_HEADERS = [
  'Sl No', 'Sub-Category ID', 'Sub-Category Name', 'Parent Category',
  'Tracking Method', 'Depreciation', 'Useful Life', 'Depr. Rate',
  'Description', 'Status', 'Actions',
];

// Skeleton Loaders
const TableSkeleton = ({ columnsCount = 5, rowsCount = 6 }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
    <Table size="medium">
      <TableHead>
        <TableRow>
          <TableCell sx={{ bgcolor: 'background.neutral' }}><Skeleton variant="text" width={40} /></TableCell>
          {Array.from({ length: columnsCount }).map((_, idx) => (
            <TableCell key={idx} sx={{ bgcolor: 'background.neutral' }}>
              <Skeleton variant="text" width={80} />
            </TableCell>
          ))}
          <TableCell align="center" sx={{ bgcolor: 'background.neutral' }}>
            <Skeleton variant="text" width={60} sx={{ mx: 'auto' }} />
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rowsCount }).map((_, rIdx) => (
          <TableRow key={rIdx}>
            <TableCell><Skeleton variant="text" width={24} /></TableCell>
            {Array.from({ length: columnsCount }).map((_, cIdx) => (
              <TableCell key={cIdx}>
                <Skeleton variant="text" width={`${60 + (cIdx * 7) % 30}%`} />
              </TableCell>
            ))}
            <TableCell align="center">
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const TabBodySkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Skeleton variant="text" width={200} height={32} sx={{ borderRadius: 1 }} />
        <Skeleton variant="text" width={280} height={20} sx={{ borderRadius: 1, mt: 0.5 }} />
      </Box>
      <Skeleton variant="rounded" width={140} height={36} sx={{ borderRadius: 2 }} />
    </Box>
    <Paper sx={{ p: 2.5, borderRadius: 3 }} variant="outlined">
      <Skeleton variant="rounded" height={40} sx={{ borderRadius: 2 }} />
    </Paper>
    <TableSkeleton />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Skeleton variant="text" width={180} height={18} />
      <Skeleton variant="rounded" width={320} height={36} sx={{ borderRadius: 2 }} />
    </Box>
  </Box>
);

const SearchBar = React.memo(({ title, value, onChange }) => (
  <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: 'background.paper' }} variant="outlined">
    <TextField
      fullWidth size="small" variant="outlined"
      placeholder={`Search ${title.toLowerCase()}s by name, ID, or other attributes...`}
      value={value} onChange={onChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: 'action.active' }} fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: value && (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => onChange({ target: { value: '' } })}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
    />
  </Paper>
));
SearchBar.displayName = 'SearchBar';

const PaginationRow = React.memo(({ filteredCount, page, rowsPerPage, onPageChange, onRowsPerPageChange }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={500}>
      Showing {filteredCount === 0 ? 0 : page * rowsPerPage + 1} to{' '}
      {Math.min((page + 1) * rowsPerPage, filteredCount)} of {filteredCount} records
    </Typography>
    <TablePagination
      rowsPerPageOptions={[5, 10, 25, 50]}
      component="div" count={filteredCount} rowsPerPage={rowsPerPage} page={page}
      onPageChange={onPageChange} onRowsPerPageChange={onRowsPerPageChange}
      sx={{ border: 'none', overflow: 'hidden' }}
    />
  </Box>
));
PaginationRow.displayName = 'PaginationRow';

const SubCategoryTable = React.memo(({ data = [], categoryMap, onEdit, renderDeleteButton, startIndex }) => {
  const isNonSerialized = (type) => String(type || '').toLowerCase() === 'non-serialized';
  const isSLM = (dep) => dep === 'straight-line' || dep === 'SLM';
  const isWDV = (dep) => dep === 'written-down-value' || dep === 'WDV';

  return (
    <TableContainer component={Paper} variant="outlined"
      sx={{ borderRadius: 3, boxShadow: '0px 4px 16px rgba(0,0,0,0.02)', overflowX: 'auto' }}>
      <Table sx={{ minWidth: 900 }} size="medium">
        <TableHead>
          <TableRow>
            {SUB_HEADERS.map((h) => (
              <TableCell key={h} align={h === 'Actions' ? 'center' : 'left'}
                sx={{ fontWeight: 700, bgcolor: 'background.neutral', whiteSpace: 'nowrap' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={SUB_HEADERS.length} align="center" sx={{ py: 8 }}>
                <Typography variant="body2" color="text.secondary">No sub-categories found</Typography>
              </TableCell>
            </TableRow>
          ) : data.map((item, idx) => {
            const isSLMethod = isSLM(item.depreciation);
            const isWDVMethod = isWDV(item.depreciation);
            const hasDep = isSLMethod || isWDVMethod;
            const fallbackId = item.id || `subcat-${idx}`;
            
            return (
              <TableRow key={fallbackId} hover>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>{startIndex + idx + 1}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'primary.main', fontSize: '0.85rem' }}>
                  {item.subCategoryId || '—'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{item.subCategoryName || '—'}</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  {categoryMap.get(item.categoryId) || 'Unknown'}
                </TableCell>
                <TableCell>
                  <Chip label={isNonSerialized(item.serializationType) ? 'Non-Serialized' : 'Serialized'}
                    size="small" color={isNonSerialized(item.serializationType) ? 'warning' : 'info'}
                    variant="outlined" sx={{ fontWeight: 500, fontSize: '0.72rem' }} />
                </TableCell>
                <TableCell>
                  {hasDep ? (
                    <Tooltip title={isSLMethod ? 'Straight-Line Method' : 'Written Down Value Method'} arrow>
                      <Chip label={isSLMethod ? 'SLM' : 'WDV'}
                        size="small" color={isWDVMethod ? 'primary' : 'success'}
                        sx={{ fontWeight: 600, fontSize: '0.72rem' }} />
                    </Tooltip>
                  ) : (<Typography variant="body2" color="text.disabled">—</Typography>)}
                </TableCell>
                <TableCell>
                  {hasDep && isSLMethod && item.usefulLife
                    ? (<Typography variant="body2" fontWeight={600}>{item.usefulLife} Yrs</Typography>)
                    : (<Typography variant="body2" color="text.disabled">—</Typography>)}
                </TableCell>
                <TableCell>
                  {hasDep && isWDVMethod && item.depreciationRate
                    ? (<Typography variant="body2" fontWeight={600}>{item.depreciationRate}%</Typography>)
                    : (<Typography variant="body2" color="text.disabled">—</Typography>)}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.82rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.description || '—'}
                </TableCell>
                <TableCell>
                  <Chip label={item.status || 'inactive'} size="small"
                    color={String(item.status).toLowerCase() === 'active' ? 'success' : 'error'}
                    sx={{ fontWeight: 600, fontSize: '0.72rem' }} />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Edit" arrow>
                      <IconButton size="small" color="primary"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(item); }}
                        sx={{ '&:hover': { bgcolor: 'primary.light', color: 'white' } }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {renderDeleteButton(item)}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
SubCategoryTable.displayName = 'SubCategoryTable';

const DefaultMasterTable = React.memo(({ data = [], columns, onEdit, renderDeleteButton, startIndex, title }) => {
  const getStatusColor = (status) => {
    const colors = { active: 'success', inactive: 'error', pending: 'warning', archived: 'default' };
    return colors[String(status).toLowerCase()] || 'default';
  };

  return (
    <TableContainer component={Paper} variant="outlined"
      sx={{ borderRadius: 3, boxShadow: '0px 4px 16px rgba(0,0,0,0.02)', overflowX: 'auto' }}>
      <Table sx={{ minWidth: 650 }} size="medium">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, bgcolor: 'background.neutral' }}>SI No</TableCell>
            {columns.map((col) => (
              <TableCell key={col.key} sx={{ fontWeight: 700, bgcolor: 'background.neutral' }}>
                {col.label}
              </TableCell>
            ))}
            <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'background.neutral' }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length + 2} align="center" sx={{ py: 8 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  No {title.toLowerCase()} records found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((item, idx) => {
              const fallbackId = item.id || `master-${idx}`;
              return (
                <TableRow key={fallbackId} hover>
                  <TableCell>{startIndex + idx + 1}</TableCell>
                  {columns.map((col) => (
                    <TableCell key={`${fallbackId}-${col.key}`}>
                      {col.render ? col.render(item)
                        : col.key === 'status' ? (
                          <Chip label={item[col.key] || 'active'} size="small"
                            color={getStatusColor(item[col.key] || 'active')}
                            sx={{ fontWeight: 500, fontSize: '0.72rem' }} />
                        ) : (item[col.key] || '-')}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => onEdit(item)}
                          sx={{ '&:hover': { bgcolor: 'primary.light', color: 'white' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {renderDeleteButton(item)}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
DefaultMasterTable.displayName = 'DefaultMasterTable';

const MasterDataPage = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState(0);
  const active = TABS[activeTab];

  const userRole = useMemo(() => normalizeRole(currentUser?.role), [currentUser?.role]);
  const isAdmin = useMemo(
    () => [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.LOCATION_ADMIN].includes(userRole),
    [userRole]
  );

   const canModifyActiveTab = useMemo(() => {
    if (active.key === 'locations' || active.key === 'customers') {
      return [ROLES.SUPERADMIN, ROLES.ADMIN].includes(userRole);
    }
    return isAdmin;
  }, [active.key, userRole, isAdmin]);

  // Main local dataset state
  const [allData, setAllData] = useState([]);  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesError, setCategoriesError] = useState(null);

  // Dynamic Lookup catalogs
  const [serializationTypes, setSerializationTypes] = useState([]);
  const [depreciationMethods, setDepreciationMethods] = useState([]);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [modal, setModal] = useState({ type: null, item: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setSearchInput('');
    setDebouncedSearch('');
    setPage(0);
  };

  // Original category fetch kept 100% unaltered
  useEffect(() => {
    if (!currentUser || active.key !== 'subcategories') return;

    SERVICE_MAP.categories.getItems()
      .then((result) => {
        setCategories(Array.isArray(result) ? result : []);
        setCategoriesError(null);
      })
      .catch((err) => setCategoriesError(err.message));
  }, [currentUser, active.key]);

  // Dynamic tracking lists populated dynamically from SVL Collection
  useEffect(() => {
    if (!currentUser || active.key !== 'subcategories') return;

    const companyId = currentUser?.companyId || '';

    singleValueListService.getItems('serialization_types', { companyId })
      .then((items) => setSerializationTypes(items || []))
      .catch((err) => console.error('[MasterDataPage] Failed to fetch serialization options:', err));

    singleValueListService.getItems('depreciation_methods', { companyId })
      .then((items) => setDepreciationMethods(items || []))
      .catch((err) => console.error('[MasterDataPage] Failed to fetch depreciation options:', err));
  }, [currentUser, active.key]);

  // Fetches full dataset ONLY when active tab or user changes
  const fetchActiveCollectionData = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      setError('User not authenticated.');
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const targetService = SERVICE_MAP[active.key];
      if (!targetService) {
        throw new Error(`Unrecognized collection name: ${active.key}`);
      }

      const rawData = await targetService.getItems();
      setAllData(Array.isArray(rawData) ? rawData : []);
    } catch (err) {
      setError(`Failed to fetch ${active.key}: ${err.message}`);
      setAllData([]);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [active.key, currentUser]);

  useEffect(() => {
    fetchActiveCollectionData();
  }, [fetchActiveCollectionData]);

  // Instant Client-side Filter
  const filteredData = useMemo(() => {
    if (!debouncedSearch) return allData;

    const s = debouncedSearch.toLowerCase();
      return allData.filter((item) =>
      [
        item.name,
        item.brandName,
        item.categoryName,
        item.subCategoryName,
        item.vendorName,
        item.customerName,
        item.locationName,
        item.departmentName,
        item.brandId,
        item.categoryId,
        item.subCategoryId,
        item.departmentId,
        item.vendorId,
        item.customerId,
        item.locationCode,
        item.description,
        item.email,
        item.phone,
        item.gstin,
        item.contactPerson,
      ].some((v) => v && String(v).toLowerCase().includes(s))
    );
  }, [allData, debouncedSearch]);

  const totalCount = filteredData.length;

  // Instant Client-side Pagination
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // Backwards compatibility alias
  const data = paginatedData;

  const handleRefresh = useCallback(async () => {
    try {
      await fetchActiveCollectionData();
      showToast(`${active.title} data refreshed successfully!`, 'success');
    } catch {
      showToast(`Failed to refresh ${active.title} data.`, 'error');
    }
  }, [fetchActiveCollectionData, showToast, active.title]);

  const categoryMap = useMemo(
    () => new Map(categories.map((c) => [c.id, `${c.categoryName} (${c.categoryId})`])),
    [categories]
  );

  const fields = useMemo(() => {
    const base = masterDataFields[active.key] || [];
    if (active.key !== 'subcategories') return base;
    return base.map((f) => {
      if (f.name === 'categoryId') {
        return {
          ...f,
          options: categories.map((c) => ({
            value: c.id,
            label: `${c.categoryName} (${c.categoryId})`
          }))
        };
      }
      if (f.name === 'serializationType') {
        return {
          ...f,
          options: serializationTypes.map((item) => ({
            value: item.value,
            label: item.label
          }))
        };
      }
      if (f.name === 'depreciation') {
        return {
          ...f,
          options: depreciationMethods.map((item) => ({
            value: item.value,
            label: item.label
          }))
        };
      }
      return f;
    });
  }, [active.key, categories, serializationTypes, depreciationMethods]);

  const columns = useMemo(() => getTableColumns(active.key), [active.key]);

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const getItemDisplayName = useCallback((item) => {
    if (!item) return `${active.title} (No item data)`;
    if (active.key === 'subcategories' && item.subCategoryName) {
      return `${active.title}: ${item.subCategoryName}`;
    }
     const priorityFields = {
      brands: ['brandName', 'name', 'title'],
      categories: ['categoryName', 'name', 'title'],
      departments: ['name', 'departmentName', 'title'],
      vendors: ['vendorName', 'name', 'companyName', 'title'],
      customers: ['customerName', 'name', 'companyName', 'title'],
      locations: ['locationName', 'name', 'title'],
    };
    const fieldsToCheck = priorityFields[active.key] || ['name', 'title', 'label'];
    for (const fieldName of fieldsToCheck) {
      const value = item[fieldName];
      if (value && typeof value === 'string' && value.trim()) {
        return `${active.title}: ${value.trim()}`;
      }
    }
    return `${active.title} (ID: ${item.id || 'N/A'})`;
  }, [active.key, active.title]);

  const handleCreateClick = () => {
    if (!canModifyActiveTab) {
      showToast(`You do not have permission to create ${active.key}.`, 'error');
      return;
    }
    setModal({ type: 'create', item: null });
  };

  const handleEditClick = useCallback((item) => {
    if (!canModifyActiveTab) {
      showToast(`You do not have permission to update ${active.key}.`, 'error');
      return;
    }
    setModal({ type: 'edit', item });
  }, [canModifyActiveTab, active.key, showToast]);

  const handleDeleteClick = useCallback((e, item) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!canModifyActiveTab) {
      showToast(`You do not have permission to delete ${active.key}.`, 'error');
      return;
    }
    setModal({ type: 'delete', item });
  }, [canModifyActiveTab, active.key, showToast]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!modal.item || deleteLoading) return;
    setDeleteLoading(true);
    try {
      const targetService = SERVICE_MAP[active.key];
      await targetService.deleteItem(modal.item.id);

      showToast(`${active.title} deleted successfully!`, 'success');
      setModal({ type: null, item: null });
      await handleRefresh();
    } catch (err) {
      showToast(`Error deleting ${active.title}: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteLoading(false);
    }
  }, [modal.item, deleteLoading, active.key, active.title, handleRefresh, showToast]);

  const handleDeleteCancel = useCallback(() => {
    if (!deleteLoading) {
      setModal({ type: null, item: null });
    }
  }, [deleteLoading]);

  const handleCreateSuccess = useCallback(async () => {
    try {
      await handleRefresh();
      setModal({ type: null, item: null });
    } catch (err) {
      showToast(`Error refreshing after create: ${err.message}`, 'error');
    }
  }, [handleRefresh, showToast]);

  const handleEditSuccess = useCallback(async () => {
    try {
      await handleRefresh();
      setModal({ type: null, item: null });
    } catch (err) {
      showToast(`Error refreshing after update: ${err.message}`, 'error');
    }
  }, [handleRefresh, showToast]);

  const handleCloseModal = useCallback(() => {
    setModal({ type: null, item: null });
  }, []);

  const renderDeleteButton = useCallback((item) => {
    if (!canModifyActiveTab) return null;
    return (
      <Tooltip title="Delete" arrow>
        <IconButton size="small" color="error"
          onClick={(e) => handleDeleteClick(e, item)}
          disabled={modal.type === 'delete' || deleteLoading}
          sx={{ '&:hover': { bgcolor: 'error.light', color: 'white' } }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }, [canModifyActiveTab, handleDeleteClick, modal.type, deleteLoading]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, borderRadius: 3 }} variant="outlined">
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.5px', mb: 1 }}>
          Master Data Management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Manage system master data including brands, categories, departments, vendors, customers and locations
        </Typography>

        <Tabs value={activeTab} onChange={handleTabChange}
          variant="scrollable" scrollButtons="auto" sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          {TABS.map((t) => <Tab key={t.key} label={`${t.title}s`} />)}
        </Tabs>

        <Box sx={{ pt: 1 }}>
          {active.key === 'subcategories' && categoriesError ? (
            <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>
              <strong>Category Loading Error:</strong> {categoriesError}
            </Alert>
          ) : null}

          {loading ? (
            <TabBodySkeleton />
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>Error: {error}</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.5px' }}>
                    {active.title} Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage {active.title.toLowerCase()} master data records
                  </Typography>
                </Box>
                {canModifyActiveTab && (
                  <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreateClick}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2.5 }} disableElevation>
                    Create {active.title}
                  </Button>
                )}
              </Box>

              <SearchBar
                title={active.title}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />

              {active.key === 'subcategories' ? (
                <SubCategoryTable
                  data={data}
                  categoryMap={categoryMap}
                  onEdit={handleEditClick}
                  renderDeleteButton={renderDeleteButton}
                  startIndex={page * rowsPerPage}
                />
              ) : (
                <DefaultMasterTable
                  data={data}
                  columns={columns}
                  onEdit={handleEditClick}
                  renderDeleteButton={renderDeleteButton}
                  startIndex={page * rowsPerPage}
                  title={active.title}
                />
              )}

              <PaginationRow
                filteredCount={totalCount}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          )}
        </Box>
      </Paper>

      <CreateEditMasterModal
        open={modal.type === 'create'}
        onClose={handleCloseModal}
        mode="create"
        collectionName={active.key}
        title={active.title}
        fields={fields}
        additionalData={{ companyId: currentUser?.companyId, company: currentUser?.company }}
        onSuccess={handleCreateSuccess}
      />

      <CreateEditMasterModal
        open={modal.type === 'edit'}
        onClose={handleCloseModal}
        mode="edit"
        collectionName={active.key}
        title={active.title}
        fields={fields}
        initialData={modal.item}
        documentId={modal.item?.id}
        additionalData={{ companyId: currentUser?.companyId, company: currentUser?.company }}
        onSuccess={handleEditSuccess}
      />

      {modal.type === 'delete' && modal.item && (
        <MasterDataDeleteModal
          key={`delete-modal-${modal.item.id}`}
          itemName={getItemDisplayName(modal.item)}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          loading={deleteLoading}
        />
      )}
    </Container>
  );
};

export default MasterDataPage;